// components/MemberCardDisplay.tsx - Corrigé pour accepter le selfie
import React from 'react';
import { Camera, QrCode } from 'lucide-react';

interface MemberData {
    member_number: string;
    first_name: string;
    last_name: string;
    date_of_birth?: string;
    created_at: string;
    badges?: string[];
    selfie_url?: string;
}

interface MemberCardDisplayProps {
    memberData: MemberData;
    qrCodeUrl: string;
    onQRClick: () => void;
    className?: string;
    selfie?: string | null; // ← AJOUT: Prop pour le selfie du flux d'adhésion
    signatureData?: string | null; // ← AJOUT: Prop pour la signature si nécessaire
}

const MemberCardDisplay: React.FC<MemberCardDisplayProps> = ({
                                                                 memberData,
                                                                 qrCodeUrl,
                                                                 onQRClick,
                                                                 className = "",
                                                                 selfie, // ← AJOUT
                                                                 signatureData // ← AJOUT
                                                             }) => {


    // ✅ CORRECTION: Priorité au selfie du flux d'adhésion, puis selfie_url de la DB
    const photoToDisplay = selfie || memberData.selfie_url;

    return (
        <div className={`w-full overflow-x-auto ${className}`}>
            <div
                id={`member-card-${memberData.member_number}`}
                className="relative bg-gradient-to-br from-blue-600 to-green-600 p-6 rounded-lg text-white overflow-hidden mx-auto"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    aspectRatio: '1.6/1',
                    minHeight: '350px'
                }}
            >
                {/* Pattern de fond */}
                <div className="absolute inset-0 opacity-10">
                    <div style={{
                        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 40px)`
                    }} className="w-full h-full"></div>
                </div>

                {/* Header avec logo et numéro */}
                <div className="relative z-10 flex justify-between items-start mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                            <img
                                src="/logo128.png"
                                alt="UDB Logo"
                                className="w-16 h-16 object-contain"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.nextElementSibling!.classList.remove('hidden');
                                }}
                            />
                            <div className="hidden w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                UDB
                            </div>
                        </div>
                        <div>
                            <span className="font-bold text-lg">UNION DEMOCRATIQUE DES BÂTISSEURS</span>
                        </div>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="relative z-10 flex items-start justify-between mb-6">
                    {/* Données membre à gauche */}
                    <div className="flex-1 pr-4">
                        {/* Nom en gros */}
                        <h3 className="font-bold text-2xl mb-2 leading-tight">
                            {memberData.first_name} {memberData.last_name}
                        </h3>

                        {memberData.member_number && (
                            <p className="text-sm opacity-80 mb-1">
                                <strong>N° Membre :</strong> {memberData.member_number}
                            </p>
                        )}

                        {/* Date de naissance SANS l'âge */}
                        {memberData.date_of_birth && (
                            <p className="text-sm opacity-80 mb-1">
                                <strong>Né(e) le :</strong> {new Date(memberData.date_of_birth).toLocaleDateString('fr-FR')}
                            </p>
                        )}

                        {/* Date d'adhésion */}
                        <p className="text-sm opacity-80 mb-4">
                            Membre depuis le {new Date(memberData.created_at).toLocaleDateString('fr-FR')}
                        </p>

                        {/* QR Code */}
                        <div className="relative group">
                            <div
                                className="w-20 h-20 bg-white rounded-lg p-2 flex items-center justify-center cursor-pointer hover:scale-105 transition-transform shadow-lg"
                                onClick={onQRClick}
                                title="QR Code - Cliquez pour agrandir"
                            >
                                {qrCodeUrl ? (
                                    <img
                                        src={qrCodeUrl}
                                        alt="QR Code Membre"
                                        className="w-full h-full object-contain"
                                        crossOrigin="anonymous"
                                    />
                                ) : (
                                    <div className="text-center">
                                        <QrCode className="w-8 h-8 text-gray-500 mx-auto mb-1"/>
                                        <span className="text-xs text-gray-500">QR Code</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Photo à droite - CORRIGÉE */}
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-yellow-400 bg-white/20 flex-shrink-0">


                        {photoToDisplay ? (
                            <img
                                src={photoToDisplay}
                                alt="Profil membre"
                                className="w-full h-full object-cover"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                    console.error('❌ Erreur chargement photo:', photoToDisplay);
                                    // En cas d'erreur, afficher l'icône par défaut
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className="w-full h-full bg-white/20 flex items-center justify-center">
                                <Camera className="w-12 h-12"/>
                            </div>
                        )}
                    </div>
                </div>

                {/* Badges */}
                {memberData.badges && memberData.badges.length > 0 && (
                    <div className="relative z-10 flex flex-wrap gap-2">
                        {memberData.badges.map((badge, index) => (
                            <span
                                key={index}
                                className="bg-yellow-400 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold"
                            >
                                {badge}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberCardDisplay;
